import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Download,
  Eye,
  FileText,
  AlertTriangle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { BrandedLoading, BrandedSpinner } from '@/components/common/BrandedLoading';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { billingApi } from '@/services/billingApi';
import type { Invoice, InvoiceStatus } from '@/types/billing';
import type { PaginatedResponse } from '@/types/subscription';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_BADGE: Record<
  InvoiceStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }
> = {
  PAID: { label: 'Paid', variant: 'default', className: 'bg-emerald-600 hover:bg-emerald-600' },
  PENDING: { label: 'Pending', variant: 'outline', className: 'border-amber-500 text-amber-600' },
  SENT: { label: 'Sent', variant: 'outline' },
  OVERDUE: { label: 'Overdue', variant: 'destructive' },
  CANCELLED: { label: 'Cancelled', variant: 'secondary' },
  REFUNDED: { label: 'Refunded', variant: 'secondary', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  PARTIALLY_PAID: { label: 'Partial', variant: 'outline', className: 'border-amber-500 text-amber-600' },
  PAYMENT_FAILED: { label: 'Failed', variant: 'destructive' },
  DRAFT: { label: 'Draft', variant: 'secondary' },
};

const formatCurrencySafe = (amount: unknown, currency = 'USD'): string => {
  const num = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
  }).format(num);
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const ALL_STATUSES: InvoiceStatus[] = [
  'PAID',
  'PENDING',
  'SENT',
  'OVERDUE',
  'CANCELLED',
  'REFUNDED',
  'PARTIALLY_PAID',
  'PAYMENT_FAILED',
  'DRAFT',
];

const PAGE_SIZE = 10;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BillingHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [invoicesResponse, setInvoicesResponse] = useState<PaginatedResponse<Invoice> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'ALL'>('ALL');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fetchInvoices = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await billingApi.getUserInvoices(user.id, page, PAGE_SIZE);
      setInvoicesResponse(data);
    } catch (err: unknown) {
      const message = (err as Error)?.message ?? 'Failed to load invoices';
      if (message.includes('Authentication required')) {
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        return;
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, page, navigate, location.pathname]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleDownloadPdf = async (invoice: Invoice) => {
    try {
      setDownloadingId(invoice.id);
      const blob = await billingApi.getInvoicePdf(invoice.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = (err as Error)?.message ?? '';
      if (message.includes('Authentication required')) {
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        return;
      }
      toast({
        title: 'Download failed',
        description: message || 'Could not download the invoice PDF.',
        variant: 'destructive',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const invoices = invoicesResponse?.content ?? [];
  const totalPages = invoicesResponse?.page?.totalPages ?? 0;
  const totalElements = invoicesResponse?.page?.totalElements ?? 0;

  // Client-side status filter (the API may not support server-side filtering)
  const filteredInvoices =
    statusFilter === 'ALL'
      ? invoices
      : invoices.filter((inv) => inv.status === statusFilter);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      <main className="py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 space-y-6">
          {/* Back nav + heading */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/billing')}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Billing
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Invoice History</h1>
                {totalElements > 0 && (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {totalElements} invoice{totalElements !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Status filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                className="text-sm border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | 'ALL')}
              >
                <option value="ALL">All statuses</option>
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_BADGE[s]?.label ?? s}
                  </option>
                ))}
              </select>
            </div>
          </motion.div>

          {/* Content */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 px-4">
              <BrandedLoading message="Loading invoices…" iconSize="md" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <AlertTriangle className="h-12 w-12 text-destructive" />
              <p className="text-destructive">{error}</p>
              <Button onClick={fetchInvoices}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardContent className="py-16 text-center space-y-3">
                  <FileText className="h-16 w-16 mx-auto text-muted-foreground" />
                  <h2 className="text-lg font-semibold">No invoices found</h2>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                    {statusFilter !== 'ALL'
                      ? `No invoices with status "${STATUS_BADGE[statusFilter]?.label ?? statusFilter}".`
                      : "You don't have any invoices yet. They will appear here after your first billing cycle."}
                  </p>
                  {statusFilter !== 'ALL' && (
                    <Button variant="outline" size="sm" onClick={() => setStatusFilter('ALL')}>
                      Clear filter
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Invoices</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredInvoices.map((invoice) => {
                          const cfg = STATUS_BADGE[invoice.status] ?? STATUS_BADGE.DRAFT;
                          return (
                            <TableRow key={invoice.id}>
                              <TableCell className="font-medium">
                                {invoice.invoiceNumber}
                              </TableCell>
                              <TableCell>{formatDate(invoice.issueDate)}</TableCell>
                              <TableCell>
                                {formatCurrencySafe(invoice.totalAmount, invoice.currency)}
                              </TableCell>
                              <TableCell>
                                <Badge variant={cfg.variant} className={cfg.className}>
                                  {cfg.label}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    View
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDownloadPdf(invoice)}
                                    disabled={downloadingId === invoice.id}
                                  >
                                    {downloadingId === invoice.id ? (
                                      <BrandedSpinner />
                                    ) : (
                                      <>
                                        <Download className="h-4 w-4 mr-1" />
                                        PDF
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile card list */}
                  <div className="md:hidden divide-y">
                    {filteredInvoices.map((invoice) => {
                      const cfg = STATUS_BADGE[invoice.status] ?? STATUS_BADGE.DRAFT;
                      return (
                        <div key={invoice.id} className="p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">{invoice.invoiceNumber}</span>
                            <Badge variant={cfg.variant} className={cfg.className}>
                              {cfg.label}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              {formatDate(invoice.issueDate)}
                            </span>
                            <span className="font-semibold">
                              {formatCurrencySafe(invoice.totalAmount, invoice.currency)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => handleDownloadPdf(invoice)}
                              disabled={downloadingId === invoice.id}
                            >
                              {downloadingId === invoice.id ? (
                                <BrandedSpinner />
                              ) : (
                                <>
                                  <Download className="h-4 w-4 mr-1" />
                                  PDF
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BillingHistoryPage;
